/**
 * Cross-module lookup stores live here. Concrete fetchers are wired by modules
 * (e.g. SMS module provides /aggregators/ fetcher; Tariffs + CDP consume it).
 */
import { createLookupStore, type LookupStore } from '../composables/useLookupStore';

export interface AggregatorLite {
  id: number;
  smsc_id?: string | null;
  description?: string | null;
  enabled?: string | null;
}

export interface TemplateLite {
  id: number;
  name?: string | null;
  template?: string | null;
  aggregator_id?: number | null;
}

export interface SenderNameLite {
  id: number;
  name?: string | null;
  value?: string | null;
  aggregator_id?: number | null;
}

export interface StreamLite {
  id: number;
  name?: string | null;
  system_id?: string | null;
}

export interface DomainLite {
  domain_id: number;
  /** Tracker domain display name (`DomainOut.name`). */
  name?: string | null;
  /** Alias of `name` for older consumers (streams UI). */
  hostname?: string | null;
}

export interface MessageTypeLite {
  id: number;
  title?: string | null;
  sender_name?: string | null;
  type?: string | null;
}

/** Billing /countries/ row (owned by Tariffs module). */
export interface CountryLite {
  country_iso: string;
  country?: string | null;
}

/** Billing /currencies/ row (owned by Tariffs module). */
export interface CurrencyLite {
  short_name: string;
  name?: string | null;
}

type Fetcher<T> = () => Promise<T[]>;

const stores: {
  aggregators?: LookupStore<AggregatorLite>;
  templates?: LookupStore<TemplateLite>;
  senderNames?: LookupStore<SenderNameLite>;
  streams?: LookupStore<StreamLite>;
  domains?: LookupStore<DomainLite>;
  messageTypes?: LookupStore<MessageTypeLite>;
  countries?: LookupStore<CountryLite>;
  currencies?: LookupStore<CurrencyLite>;
} = {};

const fetchers: {
  aggregators?: Fetcher<AggregatorLite>;
  templates?: Fetcher<TemplateLite>;
  senderNames?: Fetcher<SenderNameLite>;
  streams?: Fetcher<StreamLite>;
  domains?: Fetcher<DomainLite>;
  messageTypes?: Fetcher<MessageTypeLite>;
  countries?: Fetcher<CountryLite>;
  currencies?: Fetcher<CurrencyLite>;
} = {};

export interface LookupRegistration {
  aggregators?: Fetcher<AggregatorLite>;
  templates?: Fetcher<TemplateLite>;
  senderNames?: Fetcher<SenderNameLite>;
  streams?: Fetcher<StreamLite>;
  domains?: Fetcher<DomainLite>;
  messageTypes?: Fetcher<MessageTypeLite>;
  countries?: Fetcher<CountryLite>;
  currencies?: Fetcher<CurrencyLite>;
}

/**
 * Register cross-API lookup fetchers (called by SMS / Tariffs module on init).
 * Subsequent calls override previously registered fetchers and invalidate
 * matching stores so a prior empty noop load cannot stick forever.
 */
export function registerLookups(reg: LookupRegistration): void {
  Object.assign(fetchers, reg);
  const keys = Object.keys(reg) as (keyof LookupRegistration)[];
  for (const key of keys) {
    const store = stores[key];
    // Always invalidate: a prior noop/empty parse must not stick after real fetchers land.
    if (store) void store.reload();
  }
}

function noopFetcher<T>(): T[] { return []; }

export function getAggregatorsLookup(): LookupStore<AggregatorLite> {
  if (!stores.aggregators) {
    stores.aggregators = createLookupStore<AggregatorLite>(
      () => (fetchers.aggregators ? fetchers.aggregators() : Promise.resolve(noopFetcher<AggregatorLite>())),
      {
        idField: 'id',
        labelField: 'description',
        format: (item) => {
          const label = item.description || item.smsc_id || '—';
          return `${label} (#${item.id})`;
        },
      },
    );
  }
  return stores.aggregators;
}

export function getTemplatesLookup(): LookupStore<TemplateLite> {
  if (!stores.templates) {
    stores.templates = createLookupStore<TemplateLite>(
      () => (fetchers.templates ? fetchers.templates() : Promise.resolve(noopFetcher<TemplateLite>())),
      {
        idField: 'id',
        labelField: 'name',
        format: (item) => item.name || (item.template ? item.template.slice(0, 40) : `#${item.id}`),
      },
    );
  }
  return stores.templates;
}

export function getSenderNamesLookup(): LookupStore<SenderNameLite> {
  if (!stores.senderNames) {
    stores.senderNames = createLookupStore<SenderNameLite>(
      () => (fetchers.senderNames ? fetchers.senderNames() : Promise.resolve(noopFetcher<SenderNameLite>())),
      {
        idField: 'id',
        labelField: 'name',
        format: (item) => `${item.name || item.value || '—'} (#${item.id})`,
      },
    );
  }
  return stores.senderNames;
}

export function getStreamsLookup(): LookupStore<StreamLite> {
  if (!stores.streams) {
    stores.streams = createLookupStore<StreamLite>(
      () => (fetchers.streams ? fetchers.streams() : Promise.resolve(noopFetcher<StreamLite>())),
      {
        idField: 'id',
        labelField: 'name',
        format: (item) => `${item.name || item.system_id || '—'} (#${item.id})`,
      },
    );
  }
  return stores.streams;
}

export function getDomainsLookup(): LookupStore<DomainLite> {
  if (!stores.domains) {
    stores.domains = createLookupStore<DomainLite>(
      () => (fetchers.domains ? fetchers.domains() : Promise.resolve(noopFetcher<DomainLite>())),
      {
        idField: 'domain_id',
        labelField: 'name',
        format: (item) => {
          const label = item.name || item.hostname || '—';
          return `${label} (#${item.domain_id})`;
        },
      },
    );
  }
  return stores.domains;
}

export function getMessageTypesLookup(): LookupStore<MessageTypeLite> {
  if (!stores.messageTypes) {
    stores.messageTypes = createLookupStore<MessageTypeLite>(
      () => (fetchers.messageTypes ? fetchers.messageTypes() : Promise.resolve(noopFetcher<MessageTypeLite>())),
      {
        idField: 'id',
        labelField: 'title',
        format: (item) => {
          const title = item.title || item.sender_name || '—';
          return `${title} (#${item.id})`;
        },
      },
    );
  }
  return stores.messageTypes;
}

export function getCountriesLookup(): LookupStore<CountryLite> {
  if (!stores.countries) {
    stores.countries = createLookupStore<CountryLite>(
      () => (fetchers.countries ? fetchers.countries() : Promise.resolve(noopFetcher<CountryLite>())),
      {
        idField: 'country_iso',
        labelField: 'country',
        format: (c) => {
          const iso = (c.country_iso ?? '').toUpperCase();
          return `${c.country ?? iso} (${iso})`;
        },
      },
    );
  }
  return stores.countries;
}

export function getCurrenciesLookup(): LookupStore<CurrencyLite> {
  if (!stores.currencies) {
    stores.currencies = createLookupStore<CurrencyLite>(
      () => (fetchers.currencies ? fetchers.currencies() : Promise.resolve(noopFetcher<CurrencyLite>())),
      {
        idField: 'short_name',
        labelField: 'short_name',
        format: (c) => `${c.short_name}${c.name ? ` — ${c.name}` : ''}`,
      },
    );
  }
  return stores.currencies;
}

export type { LookupStore };
